import { useEffect, useMemo, useRef } from 'react';
import { useStore } from '@/store/useStore';
import { useHotkeys } from '@/hooks/useHotkeys';
import { TopNav } from '@/components/TopNav';
import { CrmPage } from '@/components/pages/CrmPage';
import { FormsPage } from '@/components/pages/FormsPage';
import { ReportsPage } from '@/components/pages/ReportsPage';
import { FillRatePage } from '@/components/pages/FillRatePage';
import { CalendarPage } from '@/components/pages/CalendarPage';
import { DashboardPage } from '@/components/pages/DashboardPage';
import { DirectoryPage } from '@/components/pages/DirectoryPage';
// ВРЕМЕННО СКРЫТО (блок «Финансы»). Чтобы вернуть — раскомментировать импорт и маршрут ниже.
// import { FinancePage } from '@/components/pages/FinancePage';
import { PersonnelPage } from '@/components/pages/PersonnelPage';
import { MyGamesPage } from '@/components/pages/MyGamesPage';
import { BranchesPage } from '@/components/pages/BranchesPage';
import { ProfilePage } from '@/components/pages/ProfilePage';
import { AccessPage } from '@/components/pages/AccessPage';
import { RolesMatrixPage } from '@/components/pages/RolesMatrixPage';
import { PrebookingSettingsPage } from '@/components/pages/PrebookingSettingsPage';
import { PayRatesPage } from '@/components/pages/PayRatesPage';
import { BitrixDealView } from '@/components/BitrixDealView';
import { LoginScreen } from '@/components/auth/LoginScreen';
import { RegisterScreen } from '@/components/auth/RegisterScreen';
import { Toasts } from '@/components/ui/Toasts';
import { ModalsRoot } from '@/components/modals/ModalsRoot';
import { canManageBranches } from '@/auth/permissions';

function Splash({ label }: { label: string }) {
  return (
    <div className="h-full grid place-items-center bg-surface-0 text-ink-2">
      <div className="flex items-center gap-3">
        <div className="w-3 h-3 rounded-full bg-accent animate-pulse" />
        <span className="text-sm">{label}</span>
      </div>
    </div>
  );
}

export default function App() {
  const init = useStore((s) => s.init);
  const ready = useStore((s) => s.ready);
  const authStatus = useStore((s) => s.authStatus);
  const openPage = useStore((s) => s.page);
  const currentUser = useStore((s) => s.currentUser);
  const bitrixDealId = useStore((s) => s.bitrixDealId);
  const setModal = useStore((s) => s.setModal);
  const modal = useStore((s) => s.modal);
  const theme = useStore((s) => s.settings.theme);
  const attachmentSaving = useStore((s) => s.attachmentSaving);

  // «Филиалы» и «Настройки предброни» — только руководитель сети и супер-админ. Если страницу
  // запомнили с прошлого входа (или роль понизили) — молча возвращаем в календарь. Настоящая
  // защита — на сервере (guards.canManageBranches).
  const gated = (openPage === 'branches' || openPage === 'prebooking') && !canManageBranches(currentUser);
  // «Мои игры» — личный список отработанных игр: показывать нечего, если вход не привязан
  // к карточке сотрудника (employee_id пуст). Тогда молча возвращаем в календарь.
  const noEmployeeCard = openPage === 'my_games' && !currentUser?.employee_id;
  const page = gated || noEmployeeCard ? 'calendar' : openPage;

  // Код приглашения: из query (?invite=) ИЛИ из хвоста адреса (#invite=). Хвост нужен,
  // т.к. шлюз VibeCode при проходе по ?s= может срезать query — а #-хвост переживает
  // редирект охраны и до неё вообще не доходит (см. inviteLink в adminClient).
  const inviteToken = useMemo(() => {
    const q = new URLSearchParams(window.location.search).get('invite');
    if (q) return q;
    const h = window.location.hash.replace(/^#/, '');
    return new URLSearchParams(h).get('invite');
  }, []);

  // Игра из ссылки (?deal=<id> или #deal=<id>) — так открываются номера игр из групповой
  // сводки Telegram-бота. Хвост дублирует query по той же причине, что и у invite.
  const dealFromLink = useMemo(() => {
    const q = new URLSearchParams(window.location.search).get('deal');
    if (q) return q;
    const h = window.location.hash.replace(/^#/, '');
    return new URLSearchParams(h).get('deal');
  }, []);
  // Открываем один раз: без флага модалку переоткрывало бы на каждом рендере и её нельзя
  // было бы закрыть. Ждём загрузки данных (authed + ready), иначе карточке нечего показать.
  const dealLinkUsed = useRef(false);

  useEffect(() => { void init(); }, [init]);
  // Открыть игру по ссылке (?deal=<id>) — один раз, после загрузки данных.
  useEffect(() => {
    if (!dealFromLink || dealLinkUsed.current) return;
    if (authStatus !== 'authed' || !ready) return;
    dealLinkUsed.current = true;
    setModal({ kind: 'deal', id: dealFromLink });
  }, [dealFromLink, authStatus, ready, setModal]);
  // Пока карточка игры открыта — держим её номер в адресной строке (?deal=<id>#deal=<id>),
  // чтобы ссылку на игру можно было скопировать прямо из браузера и она вела в эту игру.
  // Когда карточку закрыли — номер убираем, иначе F5 после закрытия переоткрывал бы её.
  // Пропуск шлюза ?s= и прочие параметры не трогаем: они нужны при перезагрузке и чтобы
  // ссылка сработала у коллеги. У новой (несохранённой) игры id = null — её не публикуем.
  useEffect(() => {
    if (authStatus !== 'authed' || !ready) return;
    const openDealId = modal?.kind === 'deal' && modal.id != null ? String(modal.id) : null;
    const url = new URL(window.location.href);
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
    const curDeal = url.searchParams.get('deal') ?? hash.get('deal');
    if (openDealId === curDeal) return; // уже синхронно — адрес не трогаем
    if (openDealId) { url.searchParams.set('deal', openDealId); hash.set('deal', openDealId); }
    else { url.searchParams.delete('deal'); hash.delete('deal'); }
    url.hash = hash.toString() ? `#${hash.toString()}` : '';
    window.history.replaceState(null, '', url.toString());
  }, [modal, authStatus, ready]);
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);
  // Пока идёт сохранение сделки с фото (attachmentSaving > 0) — предупреждаем перед
  // закрытием/перезагрузкой: на мобильном PUT с вложением идёт несколько секунд, а
  // пользователи успевали нажать F5 и обрывали отправку (файл не сохранялся, без ошибки).
  useEffect(() => {
    if (attachmentSaving <= 0) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [attachmentSaving]);
  useHotkeys();

  if (authStatus === 'loading') return <Splash label="Загрузка +ERP…" />;
  if (authStatus === 'anonymous') {
    return inviteToken ? <RegisterScreen token={inviteToken} /> : <LoginScreen />;
  }
  if (!ready) return <Splash label="Загрузка данных…" />;

  // Режим «эта сделка» (Этап 5): открыто из карточки сделки Битрикса → показываем
  // именно её карточку, а не общий интерфейс. Работает и на десктопе, и на телефоне.
  if (bitrixDealId) return <BitrixDealView dealId={bitrixDealId} />;

  // Единый интерфейс для компьютера и телефона: одни и те же страницы,
  // адаптированные под узкий экран (ничего не обрезается, широкие таблицы скроллятся вбок).
  return (
    <div className="h-full flex flex-col bg-surface-0 text-ink-1 overflow-hidden">
      <TopNav />
      <main className="flex-1 min-h-0 overflow-hidden">
        {page === 'dashboard' && <DashboardPage />}
        {page === 'calendar' && <CalendarPage />}
        {page === 'crm' && <CrmPage />}
        {page === 'forms' && <FormsPage />}
        {page === 'reports' && <ReportsPage />}
        {page === 'fill_rate' && <FillRatePage />}
        {/* ВРЕМЕННО СКРЫТО (блок «Финансы»): {page === 'finance' && <FinancePage />} */}
        {page === 'personnel' && <PersonnelPage />}
        {page === 'my_games' && <MyGamesPage />}
        {page === 'branches' && <BranchesPage />}
        {page === 'profile' && <ProfilePage />}
        {page === 'access' && <AccessPage />}
        {page === 'roles_matrix' && <RolesMatrixPage />}
        {page === 'prebooking' && <PrebookingSettingsPage />}
        {page === 'pay_rates' && <PayRatesPage />}
        {page === 'directory' && <DirectoryPage />}
      </main>
      <Toasts />
      <ModalsRoot />
    </div>
  );
}
